import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../lib/api';
import { formatCurrency, stageLabels } from '../lib/utils';
import {
  Users, TrendingUp, DollarSign, AlertCircle,
  ArrowUpRight, ArrowDownRight, FileText, Receipt,
} from 'lucide-react';
import {
  ComposedChart, Bar, Line, Cell, LabelList,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
  ResponsiveContainer, PieChart, Pie,
} from 'recharts';
import { Skeleton } from '../components/ui/skeleton';

const CHART_COLORS = ['#2563EB', '#16A34A', '#EA580C', '#9333EA', '#DC2626', '#0D9488'];
const RADIAN = Math.PI / 180;

function renderCountLabel({ cx, cy, midAngle, outerRadius, percent, value, index }) {
  if (percent < 0.04) return null;
  const radius = outerRadius + 16;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} textAnchor="middle" dominantBaseline="central"
      fontSize={13} fontWeight={700} fill={CHART_COLORS[index % CHART_COLORS.length]}>
      {value}
    </text>
  );
}

function TrendBadge({ value }) {
  const positive = value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-full ${positive ? 'trend-up' : 'trend-down'}`}>
      {positive ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
      {Math.abs(value)}%
    </span>
  );
}

function KpiCard({ label, value, sub, iconClass, icon: Icon, trend, delay }) {
  return (
    <div className={`kpi-card animate-fade-in ${delay}`}>
      <div className="flex items-start justify-between mb-2">
        <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground leading-tight pr-1">{label}</p>
        <div className={`w-7 h-7 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconClass}`}>
          <Icon size={15} />
        </div>
      </div>
      <div className="flex items-end gap-2">
        <p className="text-lg sm:text-2xl font-bold text-foreground leading-none">{value}</p>
        {trend !== undefined && <TrendBadge value={trend} />}
      </div>
      {sub && <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

function RevenueTooltip({ active, payload, label, data, avg }) {
  if (!active || !payload?.length) return null;
  const revenue = payload[0]?.value ?? 0;
  const entry = data?.find(d => d.month === label);
  const mom = entry?.mom ?? null;
  const diff = entry?.diff ?? null;
  const aboveAvg = avg > 0 && revenue > 0 ? Math.round(((revenue - avg) / avg) * 100) : null;
  return (
    <div className="bg-card border border-border rounded-xl shadow-xl p-4 text-sm min-w-[200px]">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{label}</p>
      <p className="text-xl font-bold text-foreground">{formatCurrency(revenue)}</p>
      {mom !== null && (
        <div className={`flex items-center gap-1 mt-2 text-xs font-semibold ${mom >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'}`}>
          {mom >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
          <span>{mom >= 0 ? '+' : ''}{mom}% vs mois préc.</span>
          {diff !== null && (
            <span className="font-normal text-muted-foreground ml-0.5">
              ({diff >= 0 ? '+' : ''}{formatCurrency(Math.abs(diff))})
            </span>
          )}
        </div>
      )}
      {aboveAvg !== null && (
        <div className="mt-2 pt-2 border-t border-border text-xs text-muted-foreground flex items-center gap-1">
          <span className={aboveAvg >= 0 ? 'text-emerald-600 dark:text-emerald-400 font-medium' : 'text-amber-600 dark:text-amber-400 font-medium'}>
            {aboveAvg >= 0 ? `+${aboveAvg}%` : `${aboveAvg}%`}
          </span>
          <span>{aboveAvg >= 0 ? 'au-dessus' : 'en-dessous'} de la moyenne</span>
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardAPI.getStats()
      .then(r => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-3 animate-fade-in" data-testid="dashboard-loading">
        <Skeleton className="h-7 w-52" />
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
          <Skeleton className="h-64 lg:col-span-3" />
          <Skeleton className="h-64 lg:col-span-2" />
        </div>
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
      </div>
    );
  }

  const pipelineData = stats?.opportunities_by_stage
    ? (Array.isArray(stats.opportunities_by_stage)
        ? stats.opportunities_by_stage
            .filter(item => item.count > 0)
            .map(item => ({ name: stageLabels[item.stage] || item.stage, value: item.count, stage: item.stage }))
        : Object.entries(stats.opportunities_by_stage)
            .filter(([, count]) => count > 0)
            .map(([stage, count]) => ({ name: stageLabels[stage] || stage, value: count, stage }))
      )
    : [];

  const monthlyData = stats?.monthly_revenue || [];
  const totalRevenue6m = monthlyData.reduce((s, d) => s + (d.revenue || 0), 0);
  const bestMonth = Math.max(...monthlyData.map(d => d.revenue || 0), 0);
  const avgRevenue = monthlyData.length > 0 ? totalRevenue6m / monthlyData.length : 0;

  const enrichedMonthlyData = monthlyData.map((d, i) => {
    const prev = i > 0 ? monthlyData[i - 1].revenue : null;
    const mom = prev !== null && prev > 0 ? Math.round(((d.revenue - prev) / prev) * 100) : null;
    const diff = prev !== null ? d.revenue - prev : null;
    return { ...d, mom, diff };
  });

  const momChange = enrichedMonthlyData[enrichedMonthlyData.length - 1]?.mom ?? null;
  const totalOppCount = pipelineData.reduce((s, d) => s + d.value, 0);

  return (
    <div className="space-y-3 animate-fade-in" data-testid="dashboard">

      {/* Title */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Dashboard Overview</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Bienvenue — voici un résumé de votre activité.</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiCard label="Revenus totaux" value={formatCurrency(stats?.won_value || 0)} sub={`${stats?.won_deals || 0} affaires conclues`} iconClass="icon-bg-green" icon={DollarSign} trend={12} delay="stagger-1" />
        <KpiCard label="Nouveaux contacts" value={stats?.total_contacts || 0} sub="clients et prospects" iconClass="icon-bg-blue" icon={Users} trend={-2} delay="stagger-2" />
        <KpiCard label="Taux de conversion" value={`${stats?.conversion_rate || 0}%`} sub="opportunités → gagnées" iconClass="icon-bg-orange" icon={TrendingUp} trend={-0.4} delay="stagger-3" />
        <KpiCard label="Factures en retard" value={stats?.overdue_invoices || 0} sub={stats?.overdue_invoices > 0 ? 'à relancer immédiatement' : 'tout est à jour'} iconClass={stats?.overdue_invoices > 0 ? 'icon-bg-red' : 'icon-bg-teal'} icon={AlertCircle} delay="stagger-4" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">

        {/* Sales Growth Chart */}
        <div className="lg:col-span-3 bg-card border border-border rounded-xl p-4 shadow-sm animate-fade-in stagger-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Croissance des ventes</h2>
              <p className="text-xs text-muted-foreground mt-0.5">6 derniers mois — factures encaissées</p>
            </div>
            {momChange !== null && <TrendBadge value={momChange} />}
          </div>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={enrichedMonthlyData} margin={{ top: 18, right: 4, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} width={42} />
                <Tooltip content={(props) => <RevenueTooltip {...props} data={enrichedMonthlyData} avg={avgRevenue} />} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.5 }} />
                {avgRevenue > 0 && (
                  <ReferenceLine y={avgRevenue} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" strokeOpacity={0.4}
                    label={{ value: 'moy.', position: 'insideTopRight', fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  />
                )}
                <Bar dataKey="revenue" radius={[4, 4, 0, 0]} maxBarSize={52}>
                  {enrichedMonthlyData.map((entry, index) => (
                    <Cell key={index}
                      fill={index === 0 ? '#93C5FD' : entry.mom >= 0 ? '#2563EB' : '#EF4444'}
                      fillOpacity={index === enrichedMonthlyData.length - 1 ? 1 : 0.75}
                    />
                  ))}
                  <LabelList dataKey="mom" content={({ x, y, width, value }) => {
                    if (value === null || value === undefined) return null;
                    const positive = value >= 0;
                    return (
                      <text x={Number(x) + Number(width) / 2} y={Number(y) - 5} textAnchor="middle" fontSize={10} fontWeight="700" fill={positive ? '#16A34A' : '#DC2626'}>
                        {positive ? '+' : ''}{value}%
                      </text>
                    );
                  }} />
                </Bar>
                <Line type="monotone" dataKey="revenue" stroke="#1D4ED8" strokeWidth={2} dot={{ fill: '#2563EB', r: 3, strokeWidth: 0 }} activeDot={{ r: 5, fill: '#2563EB', strokeWidth: 0 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          {/* Stats footer */}
          <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-border">
            <div>
              <p className="text-[10px] text-muted-foreground">Total 6 mois</p>
              <p className="text-xs font-bold text-foreground mt-0.5">{formatCurrency(totalRevenue6m)}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground">Meilleur mois</p>
              <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">{formatCurrency(bestMonth)}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground">Moyenne / mois</p>
              <p className="text-xs font-bold text-foreground mt-0.5">{formatCurrency(avgRevenue)}</p>
            </div>
          </div>
        </div>

        {/* Pipeline Pie Chart */}
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-4 shadow-sm animate-fade-in stagger-6">
          <div className="mb-2 text-center">
            <h2 className="text-sm font-semibold text-foreground">Pipeline par étape</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {totalOppCount} opportunité{totalOppCount !== 1 ? 's' : ''} en cours
            </p>
          </div>
          {pipelineData.length > 0 ? (
            <div className="flex items-center justify-between gap-2 px-2">

              {/* Donut */}
              <div className="flex-shrink-0">
                <PieChart width={220} height={220} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                    <Pie
                      data={pipelineData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={82}
                      paddingAngle={3}
                      dataKey="value"
                      stroke="none"
                      label={renderCountLabel}
                      labelLine={false}
                    >
                      {pipelineData.map((entry, index) => (
                        <Cell key={entry.stage} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v, n) => [`${v} opportunité${v > 1 ? 's' : ''}`, n]}
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }}
                    />
                  </PieChart>
              </div>

              {/* Légende à droite */}
              <div className="flex flex-col justify-center gap-1.5 self-center">
                {pipelineData.map((item, index) => {
                  const color = CHART_COLORS[index % CHART_COLORS.length];
                  const pct = totalOppCount > 0 ? Math.round((item.value / totalOppCount) * 100) : 0;
                  return (
                    <div key={item.stage} className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                      <span className="text-xs text-muted-foreground truncate">{item.name}</span>
                      <span className="text-xs font-bold tabular-nums flex-shrink-0" style={{ color }}>
                        {item.value}
                        <span className="text-[10px] font-normal text-muted-foreground ml-1">({pct}%)</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">
              Aucune opportunité
            </div>
          )}
        </div>
      </div>

      {/* Bottom stats row — 4 compact cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">

        {/* Taux de conversion */}
        <div className="bg-card border border-border rounded-xl p-3 shadow-sm animate-fade-in">
          <div className="flex items-center justify-between mb-1.5">
            <h3 className="text-xs font-semibold text-foreground">Taux de conversion</h3>
            <TrendingUp size={13} className="text-muted-foreground" />
          </div>
          <p className="text-2xl font-bold text-primary">{stats?.conversion_rate || 0}%</p>
          <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-700" style={{ width: `${Math.min(stats?.conversion_rate || 0, 100)}%` }} />
          </div>
          <p className="text-[10px] text-muted-foreground mt-1.5">Objectif : 30%</p>
        </div>

        {/* Devis en attente */}
        <div className="bg-card border border-border rounded-xl p-3 shadow-sm animate-fade-in">
          <div className="flex items-center justify-between mb-1.5">
            <h3 className="text-xs font-semibold text-foreground">Devis en attente</h3>
            <FileText size={13} className="text-muted-foreground" />
          </div>
          <p className="text-2xl font-bold text-foreground">{stats?.pending_quotes || 0}</p>
          <p className="text-[10px] text-muted-foreground mt-2">à valider ou envoyer</p>
          <div className="mt-1.5 flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            <span className="text-[10px] text-muted-foreground">En attente de validation</span>
          </div>
        </div>

        {/* Factures impayées */}
        <div className="bg-card border border-border rounded-xl p-3 shadow-sm animate-fade-in">
          <div className="flex items-center justify-between mb-1.5">
            <h3 className="text-xs font-semibold text-foreground">Factures impayées</h3>
            <Receipt size={13} className="text-muted-foreground" />
          </div>
          <p className={`text-2xl font-bold ${stats?.overdue_invoices > 0 ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400'}`}>
            {stats?.overdue_invoices || 0}
          </p>
          <p className="text-[10px] text-muted-foreground mt-2">
            {stats?.overdue_invoices > 0 ? 'à relancer' : 'tout est à jour'}
          </p>
          <div className="mt-1.5 flex items-center gap-1.5">
            <div className={`w-1.5 h-1.5 rounded-full ${stats?.overdue_invoices > 0 ? 'bg-destructive' : 'bg-emerald-500'}`} />
            <span className="text-[10px] text-muted-foreground">{stats?.pending_invoices || 0} facture(s) en cours</span>
          </div>
        </div>

        {/* Valeur du pipeline */}
        <div className="bg-card border border-border rounded-xl p-3 shadow-sm animate-fade-in">
          <div className="flex items-center justify-between mb-1.5">
            <h3 className="text-xs font-semibold text-foreground">Valeur du pipeline</h3>
            <DollarSign size={13} className="text-muted-foreground" />
          </div>
          <p className="text-xl font-bold text-foreground leading-tight">{formatCurrency(stats?.total_pipeline_value || 0)}</p>
          <p className="text-[10px] text-muted-foreground mt-2">Valeur potentielle totale</p>
          <div className="mt-1.5 flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-primary" />
            <span className="text-[10px] text-muted-foreground">{stats?.total_opportunities || 0} opportunités actives</span>
          </div>
        </div>
      </div>
    </div>
  );
}
